import {
    watch,
    onMounted,
    ref,
    Transition,
    toRaw,
    computed,
} from "vue";

import {
    useFetch,
} from "../utils.js";

import { useLocale }                    from "../locales/i18n.js";
import { Filters, serializeFilters, unserializeFilters }    from "../components/filters.js";
import { SharedLogSearchRequestQueue }  from "../utils/logSearchRequests.js";

import {
    ContextMenuProvider,
} from "../components/contextMenuProvider.js";
import { useEventSource } from "./logs/useEventSource.js";
import { useLogPreviewPanel } from "./logs/useLogPreviewPanel.js";
import { mergeLogEntities } from "./logs/useLogEntities.js";
import { LogsToolbar } from "./logs/LogsToolbar.js";
import { LogSearchProgress } from "./logs/LogSearchProgress.js";
import { LogPreviewPanel } from "./logs/LogPreviewPanel.js";
import { LogsList } from "./logs/LogsList.js";
import { LogsLoadMoreFooter } from "./logs/LogsLoadMoreFooter.js";


//////////////////////////////////////////////////
//  LOGS
//////////////////////////////////////////////////

const LogsPageLimit = 25;

function getLogCursorId(log) {
    const id = Number(log?.id);

    return Number.isFinite(id) && id >= 0
        ? id
        : null;
}

function readInitialLogSearchUrlState() {
    if (typeof window === "undefined")
        return null;

    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const query = params.get("query") ?? params.get("q");

    if (mode === "expression" && query)
        return {
            mode,
            query,
        };

    return null;
}

export default {

    setup() {
        const { i18n }              = useLocale();
        const { fetch }             = useFetch();
        const initialUrlSearch      = readInitialLogSearchUrlState();
        const refLogs               = ref([]);
        const refWrapper            = ref(null);
        const refSelectedLog        = ref(null);
        const refShowFilters        = ref(false);
        const refMutex              = ref(false);
        const refMembers            = ref(new Map());
        const refChannels           = ref(new Map());
        const refFilters            = ref(new Map());
        const refFiltersSerialized  = ref({});
        const refFilterMode         = ref(initialUrlSearch?.mode || localStorage["filters.mode"] || "structured");
        const refFilterExpression   = ref(initialUrlSearch?.query || localStorage["filters.expression"] || "");
        const refSearchError        = ref(null);
        const refSearchProgress     = ref(null);
        const refHasMoreLogs        = ref(true);
        const refRealtimeEnabled    = ref(localStorage["logs.realtime.enabled"] !== "false");
        const {
            refPreviewWidth,
            refPreviewFullscreen,
            startPreviewResize,
        } = useLogPreviewPanel();
        const refRealtimeSubscription = computed(() => (
            refFilterMode.value === "expression"
                ? {
                    mode    : "expression",
                    query   : refFilterExpression.value,
                }
                : refFiltersSerialized.value
        ));
        const source                = useEventSource();
        let currentSearchController = null;
        let currentSubscriptionSync = 0;

        source.on("log_create", (log) => {
            if (refLogs.value.length >= 300) {
                console.log("OUT.");

            } else {
                refLogs.value.unshift(log);
            }
        });

        source.on("member_update", (member) => {
            if (refMembers.value.size >= 10_000) {
                console.log("OUT.");

            } else {
                refMembers.value.set(member.id, member);
            }
        });

        source.on("channel_update", (channel) => {
            refChannels.value.set(channel.id, channel);
        });

        source.on("log_search_progress", (progress) => {
            if (!refSearchProgress.value || progress.nonce !== refSearchProgress.value.nonce)
                return;

            refSearchProgress.value = Object.assign({}, refSearchProgress.value, progress);
        });

        async function syncRealtimeSubscription() {
            const syncId = ++currentSubscriptionSync;
            const body = refRealtimeEnabled.value
                ? {
                    type    : "logs",
                    filters : refRealtimeSubscription.value,
                }
                : {
                    type: "none",
                };

            try {
                await fetch("/api/log-subscriptions", {
                    headers: {
                        "content-type": "application/json",
                    },
                    body    : JSON.stringify(body),
                    method  : "PATCH",
                });

            } catch (error) {
                if (syncId === currentSubscriptionSync)
                    console.error(error);
            }
        }

        source.on("events_ready", syncRealtimeSubscription);

        watch(
            () => [ refRealtimeEnabled.value, JSON.stringify(refRealtimeSubscription.value) ],
            syncRealtimeSubscription,
            { immediate: true }
        );

        function createSearchNonce() {
            return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }

        function cancelCurrentSearch() {
            currentSearchController?.abort();
            currentSearchController = null;
            refMutex.value = false;
            refSearchProgress.value = null;
        }

        async function fetchNext() {
            if (refMutex.value || !refHasMoreLogs.value)
                return;

            let searchController = null;

            try {
                refMutex.value = true;
                refSearchError.value = null;
                currentSearchController?.abort();
                searchController = new AbortController();
                currentSearchController = searchController;

                const searchParams = new URLSearchParams();
                const serializedFilter = refFilterMode.value === "expression"
                    ? {
                        mode    : "expression",
                        query   : refFilterExpression.value,
                    }
                    : refFiltersSerialized.value;
                const lastLog = refLogs.value.at(-1);
                const lastLogAt = lastLog?.createdAt;
                const lastLogId = getLogCursorId(lastLog);
                const timeRange = refFilterMode.value === "structured"
                    ? refFilters.value.get("time_range")
                    : null;
                const nonce = createSearchNonce();

                searchParams.set("limit", String(LogsPageLimit));

                refSearchProgress.value = {
                    nonce,
                    stage: "starting",
                };

                if (timeRange?.length === 2) {
                    const after = timeRange[0].getTime();
                    const before = lastLogAt
                        ? Math.min(lastLogAt, timeRange[1].getTime())
                        : timeRange[1].getTime();

                    searchParams.set("after", after);
                    searchParams.set("before", before);

                } else if (lastLogAt) {
                    searchParams.set("before", lastLogAt);
                }

                if (lastLogAt && lastLogId !== null && refFilterMode.value === "structured")
                    searchParams.set("cursorId", String(lastLogId));

                const res = await SharedLogSearchRequestQueue.fetchJson(`/api/logs?${searchParams.toString()}`, {
                    headers: {
                        "content-type": "application/json",
                    },
                    body    : JSON.stringify(Object.assign({}, serializedFilter, { nonce })),
                    method  : "POST",
                    signal  : searchController.signal,
                });

                if (res.error) {
                    refSearchError.value = res.error;
                    return;
                }

                mergeLogEntities(res, refMembers, refChannels);

                refLogs.value.push(...(res.result ?? []));

                if (!res.result?.length || res.result.length < LogsPageLimit)
                    refHasMoreLogs.value = false;

            } catch (error) {
                if (error?.name !== "AbortError")
                    throw error;

            } finally {
                if (currentSearchController === searchController) {
                    currentSearchController = null;
                    refMutex.value = false;
                }
            }
        }

        onMounted(async () => {
            try {
                if (initialUrlSearch) {
                    localStorage["filters.mode"] = "expression";
                    localStorage["filters.expression"] = initialUrlSearch.query;
                }

                if (localStorage["filters"]) {
                    const serialized = JSON.parse(localStorage["filters"]);

                    refFilters.value = await unserializeFilters(serialized);
                    refFiltersSerialized.value = serializeFilters(refFilters.value);
                    localStorage["filters"] = JSON.stringify(refFiltersSerialized.value);
                }

                await fetchNext();

            } catch (err) {
                console.error(err);

            }
        });

        watch(refWrapper, (wrapper) => {
            if (wrapper) {
                wrapper.onscroll = (e) => {
                    const posY      = e.target.scrollTop + e.target.offsetHeight;
                    const height    = e.target.scrollHeight;

                    if (posY >= (height - 150))
                        fetchNext();
                };
            }
        });

        function selectLog(log) {
            refSelectedLog.value = log;
        }

        function onSelectLog(log) {
            if (toRaw(refSelectedLog.value) === log) {
                refSelectedLog.value = null;

            } else {
                selectLog(log);
            }
        }

        function onUpdateFilters(values) {
            cancelCurrentSearch();

            refLogs.value               = [];
            refHasMoreLogs.value        = true;
            refFilters.value            = values;
            refFiltersSerialized.value  = serializeFilters(values);
            refFilterMode.value         = "structured";
            refShowFilters.value        = false;

            // Save in localStorage.
            localStorage["filters"] = JSON.stringify(refFiltersSerialized.value);
            localStorage["filters.mode"] = refFilterMode.value;

            fetchNext();
        }

        function onUpdateExpression(expression) {
            cancelCurrentSearch();

            refLogs.value               = [];
            refHasMoreLogs.value        = true;
            refFilterExpression.value   = expression;
            refFilterMode.value         = "expression";
            refFiltersSerialized.value  = {};
            refShowFilters.value        = false;

            localStorage["filters.expression"] = expression;
            localStorage["filters.mode"] = refFilterMode.value;

            fetchNext();
        }

        function toggleRealtime() {
            refRealtimeEnabled.value = !refRealtimeEnabled.value;
            localStorage["logs.realtime.enabled"] = refRealtimeEnabled.value ? "true" : "false";
        }

        return () => {
            return (
                <div id="app">
                    <Transition name="fade" v-slots={{ $stable:true }}>
                        {refShowFilters.value && <Filters
                            values={refFilters.value}
                            mode={refFilterMode.value}
                            expression={refFilterExpression.value}
                            onsave={onUpdateFilters}
                            onsaveExpression={onUpdateExpression}
                            key={1} />}
                    </Transition>

                    <LogsToolbar
                        filterMode={refFilterMode.value}
                        filterExpression={refFilterExpression.value}
                        filtersCount={refFilters.value.size}
                        realtimeEnabled={refRealtimeEnabled.value}
                        filterLabel={i18n("page.logs.filter_logs")}
                        onopenFilters={() => (refShowFilters.value = true)}
                        ontoggleRealtime={toggleRealtime}
                    />

                    <div class="log-panel">
                        <div class="log-list" ref={refWrapper}>
                            <LogSearchProgress
                                progress={refSearchProgress.value}
                                oncancel={cancelCurrentSearch}
                            />
                            <LogsList
                                logs={refLogs.value}
                                searchError={refSearchError.value}
                                selectedLog={refSelectedLog.value}
                                members={refMembers}
                                channels={refChannels}
                                filters={refFilters}
                                i18n={i18n}
                                onupdateFilters={onUpdateFilters}
                                onselectLog={onSelectLog}
                            />
                            <LogsLoadMoreFooter
                                loading={refMutex.value}
                                hasMore={refHasMoreLogs.value}
                                onloadMore={fetchNext}
                            />
                        </div>

                        <LogPreviewPanel
                            log={refSelectedLog.value}
                            members={refMembers}
                            channels={refChannels}
                            width={refPreviewWidth.value}
                            fullscreen={refPreviewFullscreen.value}
                            i18n={i18n}
                            onresize={startPreviewResize}
                            onselectLog={selectLog}
                            onopenFullscreen={() => (refPreviewFullscreen.value = true)}
                            oncloseFullscreen={() => (refPreviewFullscreen.value = false)}
                            onclosePreview={() => {
                                refPreviewFullscreen.value = false;
                                refSelectedLog.value = null;
                            }}
                        />
                    </div>

                    <ContextMenuProvider />
                </div>
            );
        };
    },
}
